import { ConditionalWrap } from '@libs/ui/ds'
import { usePlanningTranslation } from '@libs/utils/translations'
import { useShallowSelector } from '@spa/redux/hooks'
import {
  labelsSelectors,
  locationsSelectors,
  restsSelectors,
  shiftsSelectors,
  teamsSelectors,
  useOpenShiftModalPolicies,
  weeklyschedulesSelectors
} from '@spa/scenes/Plannings/redux/selectors'
import classnames from 'classnames'
import React from 'react'
import type { PropsWithChildren, ReactNode, FC } from 'react'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import { getCurrentMembershipSelector } from 'snap-redux/modules/memberships/selectors'

import type { ShiftCardProps } from './ShiftCard/ShiftCard'
import ShiftCard from './ShiftCard/ShiftCard'
import styles from './ShiftContainer.module.css'
import { Tooltip } from './Tooltip/Tooltip'

type Props = {
  shiftId: number
  weeklyscheduleId: number
  leftSection?: ReactNode
  content?: ReactNode
  title?: ReactNode
  outlined?: boolean
  disabled?: boolean
  classNames?: ShiftCardProps['classNames']
}
const ShiftContainerComponent: FC<PropsWithChildren<Props>> = ({
  shiftId,
  content,
  leftSection,
  weeklyscheduleId,
  title,
  outlined,
  disabled,
  classNames
}) => {
  const { t } = usePlanningTranslation()
  const allowed = useOpenShiftModalPolicies({ shiftId, weeklyscheduleId })

  const {
    account,
    membership,
    currentTeam,
    shift,
    rest,
    label,
    shiftWeeklyschedule,
    shiftTeam,
    shiftLocation,
    isUnplannedNotAllowed
  } = useShallowSelector(state => {
    const account = getCurrentAccount(state)
    const membership = getCurrentMembershipSelector(state)

    const shift = shiftsSelectors.getCachedById(state, shiftId)
    const rest = restsSelectors.getCachedById(state, shift.rest_id)
    const label = labelsSelectors.getCachedById(state, shift.label_id)

    const currentWeeklyschedule = weeklyschedulesSelectors.getCachedById(
      state,
      weeklyscheduleId
    )
    const currentTeam = teamsSelectors.getCachedById(
      state,
      currentWeeklyschedule.team_id
    )
    const shiftWeeklyschedule = weeklyschedulesSelectors.getCachedById(
      state,
      shift.weeklyschedule_id
    )
    const shiftTeam = teamsSelectors.getCachedById(
      state,
      shiftWeeklyschedule?.team_id
    )
    const shiftLocation = locationsSelectors.getCachedById(
      state,
      shiftTeam?.location_id
    )

    const isUnplannedNotAllowed =
      !allowed && shift?.planification_type === 'unscheduled'

    return {
      account,
      membership,
      currentWeeklyschedule,
      currentTeam,
      shift,
      rest,
      label,
      shiftWeeklyschedule,
      shiftTeam,
      shiftLocation,
      isUnplannedNotAllowed
    }
  })

  return (
    <ConditionalWrap
      condition={isUnplannedNotAllowed}
      wrap={wrapChildren => (
        <Tooltip message={t('weekly.card.unplannedTooltip')}>
          <div
            className={classnames({
              [styles.UnplannedNotAllowed]: isUnplannedNotAllowed
            })}
          >
            {wrapChildren}
          </div>
        </Tooltip>
      )}
    >
      <ShiftCard
        classNames={classNames}
        disabled={disabled}
        outlined={outlined}
        currentTeam={currentTeam}
        account={account}
        membership={membership}
        shift={shift}
        rest={rest}
        label={label}
        title={title}
        shiftTeam={shiftTeam}
        shiftWeeklyschedule={shiftWeeklyschedule}
        shiftLocation={shiftLocation}
        content={content}
        leftSection={leftSection}
      />
    </ConditionalWrap>
  )
}

export const ShiftContainer = React.memo(ShiftContainerComponent)
